// src/expenses.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ExpensesEntry from './expensesentry';
import ExpensesList from './expenseslist';
import ExpensesCategories from './expensescategories';
import TitleBar from './components/ui/TitleBar';
import OperationalListControls from './components/OperationalListControls';
import SavedToastOverlay from './components/SavedToastOverlay';

/* =========================================================
   DATABASE MODE - expenses save to MySQL
========================================================= */
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();
const EXPENSE_LIST_PAGE_SIZE = 50;
const EXPENSE_LIST_TIMEOUT_MS = 15000;

const isValidExpenseListResponse = (data, includeYears = false) => {
  const pagination = data?.pagination;
  return Boolean(
    data &&
    Array.isArray(data.rows) &&
    pagination &&
    Number.isInteger(pagination.page) && pagination.page >= 1 &&
    Number.isInteger(pagination.page_size) && pagination.page_size >= 1 &&
    Number.isInteger(pagination.total) && pagination.total >= 0 &&
    Number.isInteger(pagination.total_pages) && pagination.total_pages >= 1 &&
    (!includeYears || (
      Array.isArray(data.available_years) &&
      data.available_years.every((year) => Number.isInteger(year))
    ))
  );
};

/* =========================================================
   LOCAL STORAGE KEYS (categories only)
========================================================= */
const LS_PARENT_KEYS = [
  'jobflow_expense_parents',
  'jobflow_expense_parent_categories',
  'jobflow_expense_parent_groups',
  'jobflowExpenseParents',
  'jobflow_expense_parentGroups',
  'jobflow_expense_parent_list'
];

const LS_CATEGORY_KEYS = [
  'jobflow_expense_categories',
  'jobflow_expense_child_categories',
  'jobflowExpenseCategories',
  'jobflow_expense_accounting_codes',
  'jobflow_expense_codes'
];

/* =========================================================
   INDEXEDDB (Attachments stored here, NOT localStorage)
========================================================= */
const IDB_DB_NAME = 'jobflow_now_files';
const IDB_DB_VERSION = 1;
const IDB_STORE = 'expense_attachments';

const idbOpen = () =>
  new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(IDB_DB_NAME, IDB_DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });

const idbGet = async (id) => {
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(id);
      req.onsuccess = () => {
        const val = req.result || null;
        db.close();
        resolve(val);
      };
      req.onerror = () => {
        db.close();
        reject(req.error || new Error('IndexedDB read failed'));
      };
    } catch (e) {
      try { db.close(); } catch (_) {}
      reject(e);
    }
  });
};

const idbDelete = async (id) => {
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(id);
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error || new Error('IndexedDB delete failed'));
      };
    } catch (e) {
      try { db.close(); } catch (_) {}
      reject(e);
    }
  });
};

/* =========================================================
   HELPERS
========================================================= */
const safeUUID = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const toDisplayDate = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return String(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = d.getFullYear();
  return `${mm}/${dd}/${yy}`;
};

const loadFirstFound = (keys, fallback) => {
  for (const k of keys) {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed != null) return parsed;
    } catch (e) {}
  }
  return fallback;
};


const isAbsoluteUrl = (u) => /^https?:\/\//i.test(String(u || ''));

const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

/* =========================================================
   CATEGORY NORMALIZATION
========================================================= */
const normalizeParents = (rawParents) => {
  let source = rawParents;

  if (source && !Array.isArray(source) && typeof source === 'object') {
    if (Array.isArray(source.parents)) source = source.parents;
    else if (Array.isArray(source.items)) source = source.items;
    else if (Array.isArray(source.data)) source = source.data;
  }

  const arr = Array.isArray(source) ? source : [];

  if (arr.length && typeof arr[0] === 'string') {
    return arr.map((s) => ({ id: safeUUID(), name: String(s || '').trim() })).filter((p) => p.name);
  }

  return arr
    .map((p) => ({
      id: p.id ?? p.parentId ?? p.value ?? safeUUID(),
      name: (p.name ?? p.label ?? p.title ?? '').toString().trim()
    }))
    .filter((p) => p.name);
};

const normalizeCategories = (rawCats) => {
  let source = rawCats;

  if (source && !Array.isArray(source) && typeof source === 'object') {
    if (Array.isArray(source.categories)) source = source.categories;
    else if (Array.isArray(source.items)) source = source.items;
    else if (Array.isArray(source.data)) source = source.data;
  }

  const arr = Array.isArray(source) ? source : [];
  if (arr.length && typeof arr[0] === 'string') return [];

  return arr
    .map((c) => ({
      id: c.id ?? c.value ?? safeUUID(),
      name: (c.name ?? c.label ?? c.title ?? '').toString().trim(),
      parentId: c.parentId ?? c.parent_id ?? c.parent ?? c.groupId ?? c.group_id ?? ''
    }))
    .filter((c) => c.name);
};

const buildCategoryLabel = (cat, parentsById) => {
  if (!cat) return '';
  const p = cat.parentId ? parentsById[String(cat.parentId)] : null;
  if (p?.name) return `${p.name} → ${cat.name}`;
  return cat.name;
};

/* =========================================================
   DEFAULT SEED (only if user has NOTHING saved)
========================================================= */
const seedDefaults = () => {
  const p1 = { id: safeUUID(), name: 'VEHICLE' };
  const p2 = { id: safeUUID(), name: 'OFFICE' };
  const p3 = { id: safeUUID(), name: 'SUPPLIES' };
  const p4 = { id: safeUUID(), name: 'MARKETING' };
  const p5 = { id: safeUUID(), name: 'UTILITIES' };

  const parents = [p1, p2, p3, p4, p5];

  const categories = [
    { id: safeUUID(), parentId: p1.id, name: 'FUEL' },
    { id: safeUUID(), parentId: p1.id, name: 'REPAIRS / MAINTENANCE' },
    { id: safeUUID(), parentId: p1.id, name: 'INSURANCE' },

    { id: safeUUID(), parentId: p2.id, name: 'RENT' },
    { id: safeUUID(), parentId: p2.id, name: 'INTERNET' },
    { id: safeUUID(), parentId: p2.id, name: 'PHONE' },

    { id: safeUUID(), parentId: p3.id, name: 'TOOLS' },
    { id: safeUUID(), parentId: p3.id, name: 'SHOP SUPPLIES' },
    { id: safeUUID(), parentId: p3.id, name: 'SAFETY / PPE' },

    { id: safeUUID(), parentId: p4.id, name: 'ADS' },
    { id: safeUUID(), parentId: p4.id, name: 'PRINTING' },

    { id: safeUUID(), parentId: p5.id, name: 'ELECTRIC' },
    { id: safeUUID(), parentId: p5.id, name: 'WATER' }
  ];

  return { parents, categories };
};

/* =========================================================
   ONE-TIME MIGRATION
========================================================= */
const migrateGenericListIntoParents = (parentsIn, categoriesIn) => {
  const parentsArr = Array.isArray(parentsIn) ? parentsIn : [];
  const catsArr = Array.isArray(categoriesIn) ? categoriesIn : [];

  if (parentsArr.length > 0) return { parents: parentsArr, categories: catsArr, migrated: false };
  if (catsArr.length === 0) return { parents: parentsArr, categories: catsArr, migrated: false };

  const genericLike = catsArr.filter((c) => !String(c.parentId || '').trim() && String(c.name || '').trim());
  if (genericLike.length === 0) return { parents: parentsArr, categories: catsArr, migrated: false };

  const newParents = genericLike.map((c) => ({
    id: c.id || safeUUID(),
    name: String(c.name || '').trim()
  }));

  const remainingCats = catsArr.filter((c) => String(c.parentId || '').trim());
  return { parents: newParents, categories: remainingCats, migrated: true };
};

/* =========================================================
   VIEW MODAL (read-only)
========================================================= */
const ExpenseViewModal = ({ styles, isMobile, expense, onClose, onEdit, onOpenReceipt }) => {
  if (!expense) return null;

  const vendor = String(expense.vendor || '').trim() || 'EXPENSE';
  const date = String(expense.date || '').slice(0, 10);
  const amount = expense.amount ?? '';
  const category = expense.category || '';
  const notes = expense.notes || expense.description || '';

  const url =
    expense?.attachment_url ||
    expense?.attachmentUrl ||
    expense?.receipt_url ||
    expense?.receiptUrl ||
    expense?.attachment?.url ||
    null;

  const hasReceipt = !!url || !!expense?.attachment?.id;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '820px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: isMobile ? '18px' : '20px' }}>🧾</span>
            <span>VIEW EXPENSE</span>
          </div>
          <button className="jfn-exp-btn" style={styles.tinyBtnLight} onClick={onClose} type="button">
            CLOSE
          </button>
        </div>

        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ ...styles.input, background: 'white' }}>
            <div style={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '12px' }}>VENDOR</div>
            <div style={{ fontWeight: 900, fontSize: '20px', marginTop: '4px' }}>{vendor}</div>
          </div>

          <div style={styles.smallRow}>
            <div style={{ ...styles.input, background: 'white' }}>
              <div style={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '12px' }}>DATE</div>
              <div style={{ fontWeight: 900, marginTop: '4px' }}>{toDisplayDate(date)}</div>
            </div>

            <div style={{ ...styles.input, background: 'white' }}>
              <div style={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '12px' }}>AMOUNT</div>
              <div style={{ fontWeight: 900, marginTop: '4px' }}>{money(amount)}</div>
            </div>
          </div>

          <div style={{ ...styles.input, background: 'white' }}>
            <div style={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '12px' }}>CATEGORY</div>
            <div style={{ fontWeight: 900, marginTop: '4px' }}>{category || '—'}</div>
          </div>

          <div style={{ ...styles.input, background: 'white', minHeight: isMobile ? '110px' : '140px' }}>
            <div style={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '12px' }}>NOTES</div>
            <div style={{ fontWeight: 900, marginTop: '6px', whiteSpace: 'pre-wrap' }}>{notes || '—'}</div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              className="jfn-exp-btn"
              style={styles.btnLight}
              onClick={onOpenReceipt}
              type="button"
              disabled={!hasReceipt}
            >
              OPEN RECEIPT
            </button>

            <button className="jfn-exp-btn" style={styles.btnLight} onClick={onEdit} type="button">
              EDIT
            </button>

            <button className="jfn-exp-btn" style={styles.btnBlack} onClick={onClose} type="button">
              DONE
            </button>
          </div>

          {!hasReceipt ? <div style={{ fontWeight: 900, opacity: 0.8 }}>No receipt is attached for this expense.</div> : null}
        </div>
      </div>
    </div>
  );
};

const Expenses = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [parents, setParents] = useState([]);
  const [categories, setCategories] = useState([]);

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseListError, setExpenseListError] = useState('');
  const [expenseListYear, setExpenseListYear] = useState(() => new Date().getFullYear());
  const [expenseListSearchDraft, setExpenseListSearchDraft] = useState('');
  const [expenseListSearch, setExpenseListSearch] = useState('');
  const [expenseListPage, setExpenseListPage] = useState(1);
  const [expenseListPagination, setExpenseListPagination] = useState({
    page: 1,
    page_size: EXPENSE_LIST_PAGE_SIZE,
    total: 0,
    total_pages: 1
  });
  const [availableExpenseYears, setAvailableExpenseYears] = useState([]);
  const [expenseListVersion, setExpenseListVersion] = useState(0);

  const [queuedReceiptFile, setQueuedReceiptFile] = useState(null);

  // ✅ NEW: vendor state
  const [vendors, setVendors] = useState([]);
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [quickVendorForm, setQuickVendorForm] = useState({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    notes: ''
  });

  const [form, setForm] = useState({
    id: null,
    date: todayISO(),
    amount: '',
    parentId: '',
    categoryId: '',
    vendor: '',
    notes: '',
    attachment: null
  });

  const [printExpense, setPrintExpense] = useState(null);

  const [showAddParent, setShowAddParent] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showManageCats, setShowManageCats] = useState(false);

  const [newParentName, setNewParentName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');

  const [editParentId, setEditParentId] = useState('');
  const [editParentName, setEditParentName] = useState('');
  const [editCatId, setEditCatId] = useState('');
  const [editCatName, setEditCatName] = useState('');
  const [editCatParentId, setEditCatParentId] = useState('');

  const fileRef = useRef(null);
  const listTopRef = useRef(null);
  const expenseListRequestRef = useRef(0);
  const expenseYearsNeededRef = useRef(true);

  const [viewingExpense, setViewingExpense] = useState(null);

  const saveCategories = async (p, c) => {
    try {
      const response = await fetch(`${API_BASE}/api/expense-categories`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify({ parents: p || [], categories: c || [] })
      });
      if (!response.ok) throw new Error(`Save failed (HTTP ${response.status})`);
      return true;
    } catch (e) {
      console.error('Failed to save expense categories:', e);
      return false;
    }
  };

  const loadCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/expense-categories`, {
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) {
      const seeded = seedDefaults();
      setParents(seeded.parents);
      setCategories(seeded.categories);
      return;
    }
      const data = await res.json();
      const rawParents = Array.isArray(data.parents) ? data.parents : [];
      const rawCats = Array.isArray(data.categories) ? data.categories : [];

      if (rawParents.length === 0) {
        // DB empty — migrate from localStorage if present, else seed defaults
        const lsParents = normalizeParents(loadFirstFound(LS_PARENT_KEYS, []));
        const lsCats = normalizeCategories(loadFirstFound(LS_CATEGORY_KEYS, []));
        const migrated = migrateGenericListIntoParents(lsParents, lsCats);
        const seeded = migrated.parents.length === 0
          ? seedDefaults()
          : { parents: migrated.parents, categories: migrated.categories };
        setParents(seeded.parents);
        setCategories(seeded.categories);
        await saveCategories(seeded.parents, seeded.categories);
      } else {
        setParents(rawParents);
        setCategories(rawCats);
      }
    } catch (e) {
      console.error('Failed to load expense categories:', e);
    }
  };

  // ✅ NEW: load vendors from DB
  const loadVendors = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/vendors`, {
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) throw new Error(`Vendor load failed (HTTP ${res.status})`);
      const data = await res.json().catch(() => null);
      setVendors(Array.isArray(data) ? data : []);
      return true;
    } catch (e) {
      console.error('Failed to load vendors:', e);
      setVendors([]);
      return false;
    }
  };

  // ✅ NEW: quick add vendor — saves to DB, reloads vendor list, fills vendor field
  const quickAddVendor = async () => {
    if (!String(quickVendorForm.name || '').trim()) return alert('Enter vendor name.');
    try {
      const res = await fetch(`${API_BASE}/api/settings/vendors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(quickVendorForm)
      });
      if (!res.ok) throw new Error('Save failed');
      setField('vendor', String(quickVendorForm.name).trim());
      setShowQuickAddVendor(false);
      setQuickVendorForm({
        name: '',
        contactName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        notes: ''
      });
      const vendorsReloaded = await loadVendors();
      if (!vendorsReloaded) {
        alert('Vendor saved, but the vendor list could not be refreshed.');
        return;
      }
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'VENDOR ADDED!' }
      }));
    } catch (e) {
      console.error('Quick add vendor failed:', e);
      alert('Failed to save vendor.');
    }
  };

  const loadExpenses = useCallback(async ({ signal, includeYears, requestId, didTimeout }) => {
    const query = new URLSearchParams({
      page: String(expenseListPage),
      page_size: String(EXPENSE_LIST_PAGE_SIZE)
    });

    if (expenseListSearch.trim()) {
      query.set('search', expenseListSearch);
    } else {
      query.set('year', String(expenseListYear));
    }
    if (includeYears) query.set('include_years', '1');

    try {
      setLoading(true);
      setExpenseListError('');
      const res = await fetch(`${API_BASE}/api/expenses?${query.toString()}`, {
        headers: { ...getTenantHeaders() },
        signal
      });
      if (!res.ok) throw new Error(`Expense list request failed (${res.status}).`);

      const data = await res.json();
      const pagination = data?.pagination;
      if (!isValidExpenseListResponse(data, includeYears)) {
        throw new Error('Expense list returned an invalid response.');
      }

      if (requestId !== expenseListRequestRef.current) return;
      setExpenses(data.rows);
      setExpenseListPagination(pagination);

      if (includeYears) {
        setAvailableExpenseYears(
          data.available_years.map(Number).filter((year) => Number.isInteger(year))
        );
        expenseYearsNeededRef.current = false;
      }
    } catch (error) {
      if (requestId !== expenseListRequestRef.current) return;
      if (error.name === 'AbortError' && !didTimeout()) return;
      const message = didTimeout()
        ? 'Expense list request timed out. Please retry.'
        : (error?.message || 'Failed to load expenses.');
      console.error('Error loading expenses:', error);
      setExpenseListError(message);
    } finally {
      if (requestId === expenseListRequestRef.current) setLoading(false);
    }
  }, [expenseListPage, expenseListSearch, expenseListYear]);

  const refreshExpenseList = useCallback(({ includeYears = false } = {}) => {
    if (includeYears) expenseYearsNeededRef.current = true;
    setExpenseListVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    loadVendors();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!expenseListSearchDraft.trim()) {
      setExpenseListSearch('');
      setExpenseListPage(1);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setExpenseListSearch(expenseListSearchDraft.trim());
      setExpenseListPage(1);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [expenseListSearchDraft]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = expenseListRequestRef.current + 1;
    expenseListRequestRef.current = requestId;
    const includeYears = expenseYearsNeededRef.current;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, EXPENSE_LIST_TIMEOUT_MS);

    loadExpenses({
      signal: controller.signal,
      includeYears,
      requestId,
      didTimeout: () => timedOut
    }).finally(() => window.clearTimeout(timeout));

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [loadExpenses, expenseListVersion]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);


  const parentsById = useMemo(() => {
    const map = {};
    (parents || []).forEach((p) => (map[String(p.id)] = p));
    return map;
  }, [parents]);

  const sortedParents = useMemo(
    () => [...(parents || [])].sort((a, b) => compareVisibleNames(a.name, b.name)),
    [parents]
  );

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => {
      const parentComparison = compareVisibleNames(
        parentsById[String(a.parentId)]?.name,
        parentsById[String(b.parentId)]?.name
      );
      return parentComparison || compareVisibleNames(a.name, b.name);
    }),
    [categories, parentsById]
  );

  const categoriesForSelectedParent = useMemo(() => {
    const pid = String(form.parentId || '');
    if (!pid) return [];
    return (categories || [])
      .filter((c) => String(c.parentId || '') === pid)
      .sort((a, b) => compareVisibleNames(a.name, b.name));
  }, [categories, form.parentId]);

  const styles = {
    container: { padding: isMobile ? '14px' : '26px', backgroundColor: 'transparent', minHeight: '100vh' },

    panel: {
      backgroundColor: '#efd2d2',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isMobile ? '14px' : '22px',
      marginBottom: '16px'
    },

    title: { fontWeight: 900, textTransform: 'uppercase', fontSize: isMobile ? '24px' : '34px', margin: '0 0 14px 0' },
    label: { fontWeight: 900, textTransform: 'uppercase', fontSize: '13px', marginBottom: '6px', display: 'block' },

    input: { padding: '14px', border: '4px solid black', fontWeight: 900, width: '100%', fontSize: '16px', boxSizing: 'border-box' },

    topRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 220px 1fr', gap: '12px' },
    row2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' },
    row1: { display: 'grid', gridTemplateColumns: '1fr', gap: '12px' },

    btnRow: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' },

    btnBlack: { background: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    btnLight: { background: '#ffffff', color: 'black', border: '5px solid black', padding: '14px 16px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    btnDanger: { background: '#ff4444', color: 'white', border: '5px solid black', padding: '10px 14px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },

    titleBar: {
      background: '#e6bcbc',
      border: '8px solid black',
      padding: '12px 14px',
      marginBottom: '12px'
    },
    titleBarInner: {
      border: '2px solid black',
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      flexWrap: 'wrap'
    },
    titleBarLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: 900,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    titleBarCount: {
      fontWeight: 900,
      textTransform: 'uppercase'
    },

    listControlsWrap: { border: '10px solid black', padding: '12px', background: '#efd2d2', marginBottom: '16px' },
    listWrap: { border: '10px solid black', padding: '12px', paddingRight: isMobile ? '6px' : '12px', background: '#efd2d2' },

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 4500, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px' },
    modal: { background: '#efd2d2', border: '10px solid black', width: '100%', maxWidth: '760px', padding: '14px', maxHeight: '85vh', overflowY: 'auto', boxSizing: 'border-box' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '6px solid black', paddingBottom: '10px', marginBottom: '10px', fontWeight: 900, textTransform: 'uppercase', position: 'sticky', top: 0, background: '#efd2d2', zIndex: 2 },
    smallRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' },
    tinyBtn: { background: '#ff4444', color: 'white', border: '4px solid black', padding: '8px 10px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' },
    tinyBtnLight: { background: '#ffffff', color: 'black', border: '4px solid black', padding: '8px 10px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }
  };

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const resetForm = () => {
    setForm({
      id: null,
      date: todayISO(),
      amount: '',
      parentId: '',
      categoryId: '',
      vendor: '',
      notes: '',
      attachment: null
    });
    setQueuedReceiptFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const goDashboard = () => {
    window.location.href = '/dashboard';
  };

  const uploadReceiptForExpenseId = async (expenseId, file) => {
    if (!expenseId || !file) return null;

    const fd = new FormData();
    fd.append('receipt', file);

    const res = await fetch(`${API_BASE}/api/expenses/${encodeURIComponent(String(expenseId))}/receipt`, {
      method: 'POST',
      headers: { ...getTenantHeaders() },
      body: fd
    });

    if (!res.ok) {
      let msg = 'Receipt upload failed.';
      try {
        const j = await res.json();
        if (j?.error) msg = j.error;
      } catch (_) {}
      throw new Error(msg);
    }

    const data = await res.json();
    const url =
      data?.attachment_url ||
      data?.attachmentUrl ||
      data?.url ||
      data?.receipt_url ||
      data?.receiptUrl ||
      null;

    const name = data?.attachment_original_name || data?.name || file.name || 'receipt';
    const type = data?.attachment_mime || data?.type || file.type || '';
    const size = Number(data?.attachment_size || data?.size || file.size || 0);

    if (!url) return null;

    return {
      url: String(url),
      name,
      type,
      size
    };
  };

  const saveExpense = async () => {
    const amt = Number(form.amount || 0);
    if (!form.date) return alert('Please select a date.');
    if (!Number.isFinite(amt) || amt <= 0) return alert('Please enter a valid amount.');
    if (!String(form.vendor || '').trim()) return alert('Please enter a vendor.');

    const selectedCat = (categories || []).find((c) => String(c.id) === String(form.categoryId || '')) || null;
    const categoryLabel = selectedCat ? buildCategoryLabel(selectedCat, parentsById) : '';

    const payload = {
      amount: amt,
      date: form.date,
      category: categoryLabel,
      description: form.notes || '',
      vendor: String(form.vendor || '').trim()
    };

    try {
      if (form.id) {
        const putRes = await fetch(`${API_BASE}/api/expenses/${encodeURIComponent(String(form.id))}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify(payload)
        });

        if (!putRes.ok) throw new Error('Failed to update expense');

        if (queuedReceiptFile) {
          const uploadedReceipt = await uploadReceiptForExpenseId(form.id, queuedReceiptFile);
          if (!uploadedReceipt?.url) throw new Error('Receipt upload did not return a usable URL.');
          setQueuedReceiptFile(null);
        }

        refreshExpenseList({ includeYears: true });
        resetForm();
        window.dispatchEvent(new CustomEvent('jobflow:saved', {
          detail: { message: 'EXPENSE UPDATED!' }
        }));
        return;
      }

      const res = await fetch(`${API_BASE}/api/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Failed to save expense');
      const saved = await res.json().catch(() => null);
      const insertedExpenseId = Number(saved?.id);
      if (!saved?.ok || !Number.isInteger(insertedExpenseId) || insertedExpenseId <= 0) {
        refreshExpenseList({ includeYears: true });
        resetForm();
        alert('Expense saved, but the server did not return its authoritative ID. Refresh and edit the saved expense if needed.');
        return;
      }

      if (queuedReceiptFile) {
        try {
          const uploadedReceipt = await uploadReceiptForExpenseId(insertedExpenseId, queuedReceiptFile);
          if (!uploadedReceipt?.url) throw new Error('Receipt upload did not return a usable URL.');
          setQueuedReceiptFile(null);
        } catch (receiptError) {
          console.error('Expense saved but receipt upload failed:', receiptError);
          refreshExpenseList({ includeYears: true });
          resetForm();
          alert(`Expense saved, but the receipt could not be attached: ${receiptError?.message || 'Receipt upload failed.'}`);
          return;
        }
      }

      refreshExpenseList({ includeYears: true });
      resetForm();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'EXPENSE SAVED!' }
      }));
    } catch (error) {
      console.error('Error saving expense:', error);
      alert(error?.message || 'Failed to save expense to database');
    }
  };

  const editExpense = (exp) => {
    setViewingExpense(null);

    // Resolve parentId and categoryId from saved category label
    let resolvedParentId = '';
    let resolvedCategoryId = '';
    if (exp.category) {
      const matchedCat = (categories || []).find(
        (c) => buildCategoryLabel(c, parentsById) === exp.category
      );
      if (matchedCat) {
        resolvedCategoryId = String(matchedCat.id);
        resolvedParentId = String(matchedCat.parentId || '');
      }
    }

    const receiptUrl =
      exp?.attachment_url ||
      exp?.attachmentUrl ||
      exp?.receipt_url ||
      exp?.receiptUrl ||
      exp?.attachment?.url ||
      null;

    setForm({
      id: exp.id,
      date: exp.date ? String(exp.date).slice(0, 10) : todayISO(),
      amount: exp.amount ?? '',
      parentId: resolvedParentId,
      categoryId: resolvedCategoryId,
      vendor: exp.vendor || '',
      notes: exp.notes || exp.description || '',
      attachment: receiptUrl ? { url: receiptUrl, name: exp.attachment_original_name || 'receipt' } : null
    });

    setQueuedReceiptFile(null);
    if (fileRef.current) fileRef.current.value = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('DELETE THIS EXPENSE?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/expenses/${id}`, {
        method: 'DELETE',
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) throw new Error('Failed to delete expense');
      expenseYearsNeededRef.current = true;
      if (expenses.length === 1 && Number(expenseListPagination.page || expenseListPage) > 1) {
        setExpenseListPage((page) => page - 1);
      } else {
        refreshExpenseList();
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense from database');
    }
  };

  const onPickFile = async (file) => {
    if (!file) return;

    if (form.id) {
      try {
        const serverReceipt = await uploadReceiptForExpenseId(form.id, file);

        if (serverReceipt?.url) {
          setField('attachment', {
            url: serverReceipt.url,
            name: serverReceipt.name,
            type: serverReceipt.type,
            size: serverReceipt.size
          });
          setQueuedReceiptFile(null);
          refreshExpenseList();
          return;
        }
      } catch (e) {
        alert(e?.message || 'Receipt upload failed.');
      }
      return;
    }

    setQueuedReceiptFile(file);

    setField('attachment', {
      name: file.name || 'receipt',
      type: file.type || '',
      size: file.size || 0,
      pending: true
    });
  };

  const removeAttachment = async () => {
    if (queuedReceiptFile) {
      setQueuedReceiptFile(null);
    }

    if (form.attachment?.id) {
      try { await idbDelete(form.attachment.id); } catch (e) {}
    }

    setField('attachment', null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const openAttachment = async (exp) => {
    let url =
      exp?.attachment_url ||
      exp?.attachmentUrl ||
      exp?.receipt_url ||
      exp?.receiptUrl ||
      exp?.attachment?.url ||
      null;

    if (url) {
      url = String(url);
      const finalUrl = isAbsoluteUrl(url) ? url : `${API_BASE}${url}`;
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const att = exp?.attachment;
    if (!att?.id) {
      alert('No receipt is attached for this expense.');
      return;
    }

    try {
      const rec = await idbGet(att.id);
      if (!rec?.blob) {
        alert('Attachment not found on this device.');
        return;
      }
      const objUrl = URL.createObjectURL(rec.blob);
      window.open(objUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objUrl), 60 * 1000);
    } catch (e) {
      alert('Could not open attachment.');
    }
  };

  const addParent = async () => {
    const name = (newParentName || '').trim();
    if (!name) return alert('Enter a parent name.');
    const newP = { id: safeUUID(), name };
    const newParents = [newP, ...(parents || [])];
    setParents(newParents);
    const saved = await saveCategories(newParents, categories);
    if (saved) {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'PARENT ADDED!' }
      }));
    }
    setNewParentName('');
    setShowAddParent(false);
  };

  const addCategory = async () => {
    const pid = String(newCategoryParentId || '').trim();
    const name = (newCategoryName || '').trim();
    if (!pid) return alert('Select a parent first.');
    if (!name) return alert('Enter a category name.');
    const newC = { id: safeUUID(), parentId: pid, name };
    const newCategories = [newC, ...(categories || [])];
    setCategories(newCategories);
    const saved = await saveCategories(parents, newCategories);
    if (saved) {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'CATEGORY ADDED!' }
      }));
    }
    setNewCategoryName('');
    setNewCategoryParentId('');
    setShowAddCategory(false);
  };

  const startEditParent = (p) => {
    setEditParentId(String(p?.id || ''));
    setEditParentName(String(p?.name || ''));
  };

  const saveEditParent = async () => {
    const id = String(editParentId || '');
    const name = String(editParentName || '').trim();
    if (!id) return;
    if (!name) return alert('Parent name required.');
    const newParents = (parents || []).map((p) => (String(p.id) === id ? { ...p, name } : p));
    setParents(newParents);
    const saved = await saveCategories(newParents, categories);
    if (saved) {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'PARENT UPDATED!' }
      }));
    }
    setEditParentId('');
    setEditParentName('');
  };

  const startEditCategory = (c) => {
    setEditCatId(String(c?.id || ''));
    setEditCatName(String(c?.name || ''));
    setEditCatParentId(String(c?.parentId || ''));
  };

  const saveEditCategory = async () => {
    const id = String(editCatId || '');
    const name = String(editCatName || '').trim();
    const pid = String(editCatParentId || '').trim();
    if (!id) return;
    if (!pid) return alert('Select a parent.');
    if (!name) return alert('Category name required.');
    const newCategories = (categories || []).map((c) => (String(c.id) === id ? { ...c, name, parentId: pid } : c));
    setCategories(newCategories);
    const saved = await saveCategories(parents, newCategories);
    if (saved) {
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'CATEGORY UPDATED!' }
      }));
    }
    setEditCatId('');
    setEditCatName('');
    setEditCatParentId('');
  };

  const deleteParent = (parentId) => {
    if (!window.confirm('DELETE THIS PARENT GROUP? (It will delete its categories too.)')) return;
    const pid = String(parentId || '');
    const newParents = (parents || []).filter((p) => String(p.id) !== pid);
    const newCategories = (categories || []).filter((c) => String(c.parentId) !== pid);
    setParents(newParents);
    setCategories(newCategories);
    saveCategories(newParents, newCategories);
    if (String(form.parentId || '') === pid) {
      setField('parentId', '');
      setField('categoryId', '');
    }
  };

  const deleteCategory = (catId) => {
    if (!window.confirm('DELETE THIS CATEGORY?')) return;
    const cid = String(catId || '');
    const newCategories = (categories || []).filter((c) => String(c.id) !== cid);
    setCategories(newCategories);
    saveCategories(parents, newCategories);
    if (String(form.categoryId || '') === cid) setField('categoryId', '');
  };

  const viewExpense = (exp) => {
    setViewingExpense(exp);
  };

  return (
    <div className="jfn-expenses-page" style={styles.container}>
      <SavedToastOverlay accentColor="#E6BCBC" />
      <style>{`
        @media (max-width: 767px) {
          .jfn-expenses-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
        .jfn-exp-btn {
          transition: transform 120ms ease, filter 120ms ease, border-color 120ms ease;
        }
        .jfn-exp-btn:hover {
          transform: translateY(-1px);
          filter: brightness(1.03);
          background: #FFC107 !important;
          color: black !important;
          border-color: black !important;
        }
      `}</style>

      <TitleBar
        icon="🧾"
        title="POST EXPENSE"
        accent="#B00020"
        right={
          <button className="jfn-exp-btn" style={{ ...styles.btnLight, background: 'black', color: '#FFA500', border: '5px solid #E8761D' }} onClick={goDashboard} type="button">
            ← BACK TO DASHBOARD
          </button>
        }
      />

      <div style={styles.panel}>
        <ExpensesEntry
          styles={styles}
          isMobile={isMobile}
          form={form}
          setField={setField}
          parents={sortedParents}
          categoriesForSelectedParent={categoriesForSelectedParent || []}
          openAddParent={() => setShowAddParent(true)}
          openAddCategory={() => setShowAddCategory(true)}
          openManage={() => setShowManageCats(true)}
          saveExpense={saveExpense}
          resetForm={resetForm}
          onPickFile={onPickFile}
          removeAttachment={removeAttachment}
          fileRef={fileRef}
          vendors={vendors}
          showQuickAddVendor={showQuickAddVendor}
          setShowQuickAddVendor={setShowQuickAddVendor}
          quickVendorForm={quickVendorForm}
          setQuickVendorForm={setQuickVendorForm}
          quickAddVendor={quickAddVendor}
        />
      </div>

      <div ref={listTopRef}>
        <TitleBar
          icon="🧾"
          title={`EXPENSE LIST (${Number(expenseListPagination.total || 0)})`}
          accent="#B00020"
          wrapStyle={{ background: '#efd2d2' }}
        />
      </div>

      <div style={styles.listControlsWrap}>
        <OperationalListControls
          moduleName="Expenses"
          searchPlaceholder="Vendor, Category, Amount, Date"
          showResultCount={false}
          theme={{
            background: '#efd2d2',
            border: '#B00020',
            accent: '#B00020',
            text: 'black',
            inputBackground: 'white',
            inputText: 'black',
            icon: 'black',
            buttonBackground: '#B00020',
            buttonText: 'white',
            buttonHover: '#B00020',
            otherYearButtonBorder: 'black',
            otherYearButtonHover: '#FFC107',
            divider: '#B00020',
            error: '#B00020'
          }}
          selectedYear={expenseListYear}
          availableYears={availableExpenseYears}
          search={expenseListSearchDraft}
          isSearchActive={Boolean(expenseListSearch.trim())}
          pagination={expenseListPagination}
          onYearChange={(year) => {
            setExpenseListYear(year);
            setExpenseListPage(1);
          }}
          onSearchChange={setExpenseListSearchDraft}
          onPageChange={(page) => {
            if (page < 1 || page > Number(expenseListPagination.total_pages || 1)) return;
            setExpenseListPage(page);
          }}
        />
      </div>

      {expenseListError ? (
        <div style={{ border: '4px solid #B00020', background: 'white', padding: '12px', marginBottom: '12px', fontWeight: 900 }} role="alert">
          <div>{expenseListError}</div>
        </div>
      ) : null}

      <div style={styles.listWrap}>
        <ExpensesList
          styles={styles}
          isMobile={isMobile}
          loading={loading}
          expenses={expenses || []}
          money={money}
          toDisplayDate={toDisplayDate}
          editExpense={editExpense}
          deleteExpense={deleteExpense}
          openAttachment={openAttachment}
          viewExpense={viewExpense}
          printExpense={printExpense}
          setPrintExpense={setPrintExpense}
          apiBase={API_BASE}
        />
      </div>

      <ExpensesCategories
        styles={styles}
        parents={sortedParents}
        categories={sortedCategories}
        parentsById={parentsById}
        buildCategoryLabel={buildCategoryLabel}
        showAddParent={showAddParent}
        setShowAddParent={setShowAddParent}
        showAddCategory={showAddCategory}
        setShowAddCategory={setShowAddCategory}
        showManageCats={showManageCats}
        setShowManageCats={setShowManageCats}
        newParentName={newParentName}
        setNewParentName={setNewParentName}
        newCategoryName={newCategoryName}
        setNewCategoryName={setNewCategoryName}
        newCategoryParentId={newCategoryParentId}
        setNewCategoryParentId={setNewCategoryParentId}
        addParent={addParent}
        addCategory={addCategory}
        editParentId={editParentId}
        setEditParentId={setEditParentId}
        editParentName={editParentName}
        setEditParentName={setEditParentName}
        editCatId={editCatId}
        setEditCatId={setEditCatId}
        editCatName={editCatName}
        setEditCatName={setEditCatName}
        editCatParentId={editCatParentId}
        setEditCatParentId={setEditCatParentId}
        startEditParent={startEditParent}
        saveEditParent={saveEditParent}
        startEditCategory={startEditCategory}
        saveEditCategory={saveEditCategory}
        deleteParent={deleteParent}
        deleteCategory={deleteCategory}
      />

      {viewingExpense ? (
        <ExpenseViewModal
          styles={styles}
          isMobile={isMobile}
          expense={viewingExpense}
          onClose={() => setViewingExpense(null)}
          onEdit={() => editExpense(viewingExpense)}
          onOpenReceipt={() => openAttachment(viewingExpense)}
        />
      ) : null}
    </div>
  );
};

export default Expenses;
